export type ChatTimelineItem = {
  id: string;
  seq: number;
  text?: string;
  createdAt?: string;
  own: boolean;
  kind: string;
  resourceId?: string;
  optimistic?: boolean;
  failed?: boolean;
};

export type ChatTimeline = {
  items: ChatTimelineItem[];
  cursors?: { firstSeq?: number; lastSeq?: number };
};

export type ConversationUnreadState = {
  items: Array<{ id: string; unreadCount: number } & Record<string, unknown>>;
} & Record<string, unknown>;

export function clearConversationUnread<T extends ConversationUnreadState>(
  current: T | undefined,
  conversationId: string,
): T | undefined {
  if (!current) return current;
  return {
    ...current,
    items: current.items.map((item) =>
      item.id === conversationId && item.unreadCount !== 0 ? { ...item, unreadCount: 0 } : item,
    ),
  };
}

export function lastConfirmedSeq(timeline: ChatTimeline | undefined): number | undefined {
  return timeline?.items.filter((item) => !item.optimistic).at(-1)?.seq;
}

export function mergeChatTimeline(
  current: ChatTimeline | undefined,
  incoming: ChatTimeline,
): ChatTimeline {
  const items = new Map<string, ChatTimelineItem>();
  for (const item of current?.items ?? []) items.set(item.id, item);
  for (const item of incoming.items) items.set(item.id, item);
  const merged = [...items.values()].sort((left, right) => left.seq - right.seq);
  const confirmed = merged.filter((item) => !item.optimistic);
  const cursors: NonNullable<ChatTimeline['cursors']> = {};
  const firstConfirmed = confirmed[0];
  const lastConfirmed = confirmed.at(-1);
  if (firstConfirmed) cursors.firstSeq = firstConfirmed.seq;
  if (lastConfirmed) cursors.lastSeq = lastConfirmed.seq;
  return {
    items: merged,
    cursors,
  };
}

export async function recoverChatTimeline(
  current: ChatTimeline | undefined,
  fetchPage: (afterSeq: number | undefined) => Promise<ChatTimeline>,
  pageSize = 50,
): Promise<ChatTimeline> {
  if (!current) return fetchPage(undefined);
  let recovered = current;
  let cursor = lastConfirmedSeq(recovered);
  for (let pageNumber = 0; pageNumber < 20; pageNumber += 1) {
    const page = await fetchPage(cursor);
    recovered = mergeChatTimeline(recovered, page);
    const nextCursor = lastConfirmedSeq(recovered);
    if (page.items.length < pageSize || nextCursor === cursor) break;
    cursor = nextCursor;
  }
  return recovered;
}
